import React from "react";
import PropTypes from "prop-types";
import { LottiePlayer } from "@learningblocks/shared-components";
import { Utilities } from "@learningblocks/exp-constants";
import {
  ORIENTATION,
  EVENT_RESIZE,
  DEFAULT_DIMENSION_UPDATE_EVENT,
  calculateDimentions,
  setUseState,
} from "./AnimationBlocks.utils";
import { AnimationBlockLoader as Loader } from "./AnimationBlock.loader";
import cname from "../style.module.scss";

const { throttle } = Utilities;
const animationBlockRef = React.createRef();

export function AnimationBlock(props) {
  const [state, setState] = React.useState(setUseState(props.orientation));

  const isOrientationAuto = () => props.orientation === ORIENTATION.AUTO;

  const isBlockRefCurrent = () =>
    animationBlockRef && animationBlockRef.current;

  const onResize = (event) =>
    throttle(() => updateDimentionsAndOrientation(event));

  const updateDimentionsAndOrientation = ({ target }) => {
    let orientation = state.orientation;
    let dimentions = state.dimentions;
    if (isOrientationAuto()) {
      const { innerHeight: height, innerWidth: width } = target;
      if (width > height) {
        orientation = ORIENTATION.WIDE;
      } else {
        orientation = ORIENTATION.SQUARE;
      }
    }
    if (isBlockRefCurrent()) {
      dimentions = calculateDimentions(animationBlockRef, orientation);
    }
    setState({ dimentions, orientation });
  };

  React.useEffect(() => {
    updateDimentionsAndOrientation(DEFAULT_DIMENSION_UPDATE_EVENT);
    window.addEventListener(EVENT_RESIZE, onResize);
    return () => {
      window.removeEventListener(EVENT_RESIZE, onResize);
    };
  }, []);

  React.useEffect(() => {
    updateDimentionsAndOrientation(DEFAULT_DIMENSION_UPDATE_EVENT);
  }, [animationBlockRef.current]);

  const setAnimationData = () => {
    if (props.src.isPath) {
      return { path: props.src[state.orientation] };
    } else {
      return { animationData: props.src[state.orientation] };
    }
  };

  const setPlayerStyle = () => ({
    width: `${state.dimentions.width}px`,
    height: `${state.dimentions.height}px`,
    overflow: "hidden",
  });

  const className = `${cname.animeBlock} ${props.className}`;
  return (
    <div className={className} ref={animationBlockRef}>
      {props.loading ? (
        <Loader style={setPlayerStyle()} orientation={state.orientation} />
      ) : (
        <LottiePlayer
          loop={false}
          autoplay={false}
          showContorls={false}
          style={setPlayerStyle()}
          {...setAnimationData()}
          currentFrame={props.currentFrame}
          animationLoaded={props.animationLoaded}
        />
      )}
    </div>
  );
}

AnimationBlock.propTypes = {
  /**
   * ClassName to set on componet
   */
  className: PropTypes.string,
  /**
   * Animation files to render on wide/square orientation
   */
  src: PropTypes.shape({
    /**
     * Boolean specifying if animations provided are url-path or animation-json
     */
    isPath: PropTypes.bool,
    /**
     * Animation file for 16:9 orientation
     */
    wide: PropTypes.object,
    /**
     * Animation file to 1:1 orientation
     */
    square: PropTypes.object,
  }).isRequired,
  /**
   * Orientation for Animation
   */
  orientation: PropTypes.oneOf(["wide", "square", "auto"]).isRequired,
  /**
   * Current frame of animation displayed
   */
  currentFrame: PropTypes.number,
  /**
   * Callback for when animation file is loaded
   */
  animationLoaded: PropTypes.func,
  /**
   * Set to show Content loader for the component
   */
  loading: PropTypes.bool,
};

AnimationBlock.defaultProps = {
  className: "",
  orientation: "auto",
};
